import { createElement, Fragment, useRef, useState } from 'react';
import { TextColor, FontStyle } from '../../../theme';
import { SliderGripLabel } from '../GripLabel/SliderGripLabel';
import { SliderRange } from './SliderRange';
import { useSliderDrag, SliderTrackClientBounds } from '../useSliderDrag';
import { SliderScale } from '../SliderScale';
import { SliderFormat } from '../SliderFormat';
import { ReactMouseEvent } from '../../../react-synthetic-events';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { SliderGripKeyAction } from '../ValueGrip/SliderGrip';
import { useIsRTL } from '../../../locale';
import { calculatePreciseValue, SliderStep } from '..';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useFormatLocale } from '../../../locale/formatting';
export interface SliderRangeGripProps extends TestIdProps {
  gripLabelClassName: string | undefined;
  startValue: number;
  endValue: number;
  minValue: number;
  maxValue: number;
  ariaLabel?: string;

  stepValue: SliderStep;
  stepMediumValue: SliderStep;
  stepLargeValue: SliderStep;

  clientBounds: SliderTrackClientBounds;
  orientation: 'horizontal' | 'vertical';
  format: SliderFormat;
  scale: SliderScale;
  disableRoundToStep?: boolean;

  hideLabel: boolean;
  disabled?: boolean;
  readOnly?: boolean;

  handleLabelTokens:
    | undefined
    | null
    | {
        theme?: () => Partial<TextColor> | undefined;
        font?: Partial<FontStyle>;
      };

  onChange?: (event: { startValue: number; endValue: number }) => void;
  onCommit?: (event: { startValue: number; endValue: number }) => void;
  onPress?: (event: ReactMouseEvent) => void;
  sliderGripControlIsVisible?: boolean;
}

const useHtmlId = createUseHtmlId('sliderRange');

export const SliderRangeGrip = (props: SliderRangeGripProps) => {
  const localize = useNCTextLocalizer('Slider');
  const numberFormat = Intl.NumberFormat(useFormatLocale());
  const refDragStartState = useRef({ startPos: NaN, endPos: NaN });

  const posHalfLength =
    0.5 * (props.scale.position(props.endValue) - props.scale.position(props.startValue));

  const { startDrag, lastDragStartPos, didDrag } = useSliderDrag({
    clientBounds: props.clientBounds,
    orientation: props.orientation,
    minPosition: props.scale.position(props.minValue) + posHalfLength,
    maxPosition: props.scale.position(props.maxValue) - posHalfLength,
    onChange(e) {
      const deltaPos = e.position - lastDragStartPos;
      props.onChange &&
        props.onChange({
          startValue: props.scale.value(refDragStartState.current.startPos + deltaPos, didDrag),
          endValue: props.scale.value(refDragStartState.current.endPos + deltaPos, didDrag),
        });
    },
    onCommit(e) {
      const deltaPos = e.position - lastDragStartPos;
      props.onCommit &&
        props.onCommit({
          startValue: props.scale.value(refDragStartState.current.startPos + deltaPos, didDrag),
          endValue: props.scale.value(refDragStartState.current.endPos + deltaPos, didDrag),
        });
    },
  });

  const startPos = props.scale.position(props.startValue);
  const endPos = props.scale.position(props.endValue);
  const midPos = 0.5 * (startPos + endPos);

  const startLabel = props.format.format(props.startValue);
  const endLabel = props.format.format(props.endValue);
  const label =
    props.orientation === 'vertical'
      ? [endLabel, startLabel]
      : localize('sliderRangeGrip.gripLabel.label', { startLabel, endLabel });

  const refRange = useRef<HTMLDivElement>(null);

  const [enableFocusOutline, setEnableFocusOutline] = useState(true);

  const isRTL = useIsRTL();

  const onKeyAction = ({ type, dir }: SliderGripKeyAction) => {
    let minValue: number;
    let maxValue: number;
    const range = props.endValue - props.startValue;
    const minEndValue = props.minValue + range;
    const maxStartValue = props.maxValue - range;

    if (type === 'stepMax') {
      minValue = dir === 'forward' ? maxStartValue : props.minValue;
      maxValue = dir === 'forward' ? props.maxValue : minEndValue;
    } else {
      let step: SliderStep = 0;
      if (type === 'step') {
        step = props.stepValue;
      } else if (type === 'stepTwo') {
        step = props.stepMediumValue;
      } else if (type === 'stepLarge') {
        step = props.stepLargeValue;
      }

      minValue = props.startValue;
      maxValue = props.endValue;
      if (typeof step === 'number') {
        minValue += (dir === 'forward' ? 1 : -1) * step;
        minValue = calculatePreciseValue(
          minValue,
          props.stepValue,
          numberFormat,
          props.disableRoundToStep
        );
        maxValue += (dir === 'forward' ? 1 : -1) * step;
        maxValue = calculatePreciseValue(
          maxValue,
          props.stepValue,
          numberFormat,
          props.disableRoundToStep
        );
      } else {
        minValue = step(minValue, { type: dir });
        maxValue = step(maxValue, { type: dir });
      }
      if (minValue > maxStartValue) {
        minValue = maxStartValue;
      } else if (minValue < props.minValue) {
        minValue = props.minValue;
      }
      if (maxValue > props.maxValue) {
        maxValue = props.maxValue;
      } else if (maxValue < minEndValue) {
        maxValue = minEndValue;
      }
    }

    props.onChange && props.onChange({ startValue: minValue, endValue: maxValue });
    if (props.onCommit) props.onCommit({ startValue: minValue, endValue: maxValue });
  };

  const invisibleTextId = useHtmlId();

  return (
    <Fragment>
      <SliderGripLabel
        testId={postfixTestId(props.testId, 'label')}
        disabled={props.disabled}
        className={props.gripLabelClassName}
        orientation={props.orientation}
        position={midPos}
        label={label}
        hideLabel={props.hideLabel}
        handleLabelTokens={props.handleLabelTokens}
        // zIndex will place this label above other containers to allow users to see its tooltip if it is truncated
        containerStyle={{ zIndex: !props.sliderGripControlIsVisible ? 1 : undefined }}
      />
      <SliderRange
        testId={props.testId}
        disabled={props.disabled}
        ref={refRange}
        tabIndex={0}
        xStyle={{
          orientation: props.orientation,
          minPos: startPos,
          maxPos: endPos,
          enableFocusOutline,
        }}
        onMouseDown={
          props.disabled || props.readOnly
            ? undefined
            : (e) => {
                // only process left clicks
                if (e.button !== 0) return;
                // need to prevent default action, which starts a native DOM drag-and-drop sometimes.
                e.preventDefault();

                refDragStartState.current.startPos = startPos;
                refDragStartState.current.endPos = endPos;
                startDrag([e.clientX, e.clientY], midPos);
                if (refRange.current) {
                  // focus the grip so it can receive keyboard input and closes any grip inputs
                  refRange.current.focus();
                }
                // don't show focus outline when the user drags a grip
                setEnableFocusOutline(false);
              }
        }
        onClick={
          props.disabled || props.readOnly
            ? undefined
            : (e) => {
                if (!didDrag && props.onPress) {
                  props.onPress(e);
                }
              }
        }
        onKeyDown={
          props.disabled || props.readOnly
            ? undefined
            : (e) => {
                const handleKeyAction = (action: SliderGripKeyAction) => {
                  // NOTE: each of these keys could cause scrolling to occur, so we preventDefault in those cases.
                  e.preventDefault();
                  // show focus outline when the user uses the keyboard
                  setEnableFocusOutline(true);
                  onKeyAction(action);
                };
                switch (e.key) {
                  case 'ArrowRight':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: isRTL ? 'backward' : 'forward',
                    });
                    break;
                  case 'ArrowUp':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: 'forward',
                    });
                    break;
                  case 'ArrowLeft':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: isRTL ? 'forward' : 'backward',
                    });
                    break;
                  case 'ArrowDown':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: 'backward',
                    });
                    break;
                  case 'Home':
                    handleKeyAction({ type: 'stepMax', dir: 'backward' });
                    break;
                  case 'End':
                    handleKeyAction({ type: 'stepMax', dir: 'forward' });
                    break;
                  case 'PageUp':
                    handleKeyAction({ type: 'stepLarge', dir: 'forward' });
                    break;
                  case 'PageDown':
                    handleKeyAction({ type: 'stepLarge', dir: 'backward' });
                    break;
                }
              }
        }
        onBlur={
          props.disabled || props.readOnly
            ? undefined
            : () => {
                // reset focus, so if a user tabs into the slider grip it'll show an outline
                setEnableFocusOutline(true);
              }
        }
        role="slider"
        ariaLabel={props.ariaLabel ?? localize('sliderRangeGrip.sliderRange.aria')}
        aria-labelledby={invisibleTextId!}
        aria-disabled={props.disabled}
        aria-readonly={props.readOnly}
        aria-orientation={props.orientation}
        aria-valuenow={(props.startValue + props.endValue) * 0.5}
        aria-valuemin={props.startValue}
        aria-valuemax={props.endValue}
        aria-valuetext={localize('sliderRangeGrip.sliderRange.valueText.aria', {
          startLabel,
          endLabel,
        })}
      />
    </Fragment>
  );
};
